import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useDateRange } from './DateRangeContext';
import { endOfMonth, format } from 'date-fns';

const PRODUCT_CATEGORIES = [
  'Wisdrop',
  'Contracts', 
  'zevioo',
  'Brand Beat',
  'Qualitative',
  'Other Ad-Hoc'
];

interface CategorySales {
  category: string;
  currentYearSales: number;
  previousYearSales: number;
  growth: number;
  growthPercentage: number;
}

const SalesOverviewSkeleton = () => (
  <Card>
    <CardHeader>
      <CardTitle>Sales Overview</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        {PRODUCT_CATEGORIES.map((category) => (
          <div key={category} className="p-4 border rounded-lg">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-6 w-16 mb-1" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function SalesOverview() {
  const { selectedYear, dateRange } = useDateRange();
  const previousYear = selectedYear - 1;
  
  // For comparison: use same full months of previous year
  const currentYear = new Date().getFullYear();
  const isCurrentYear = selectedYear === currentYear;
  const toMonth = dateRange.to.getMonth(); // 0-indexed
  
  const { data: salesData, isLoading, error } = useQuery({
    queryKey: ['sales-overview', selectedYear, previousYear, toMonth],
    queryFn: async () => {
      // Current year: use full months up to current month
      const startOfSelectedYear = `${selectedYear}-01-01`;
      const endOfCurrentPeriod = isCurrentYear 
        ? format(endOfMonth(new Date(selectedYear, toMonth, 1)), 'yyyy-MM-dd')
        : `${selectedYear}-12-31`;
      
      // Previous year: always compare with same full months
      const startOfPreviousYear = `${previousYear}-01-01`;
      const endOfPreviousPeriod = isCurrentYear
        ? format(endOfMonth(new Date(previousYear, toMonth, 1)), 'yyyy-MM-dd')
        : `${previousYear}-12-31`;
      
      // Get current year sales by category from invoices
      // Use left join to include invoices without items (legacy invoices)
      const { data: currentYearData, error: currentError } = await supabase
        .from('invoices')
        .select(`
          amount,
          discount_percentage,
          title,
          invoice_items(
            quantity,
            unit_price,
            products(
              product_categories(name)
            )
          )
        `)
        .gte('issue_date', startOfSelectedYear)
        .lte('issue_date', endOfCurrentPeriod)
        .not('issue_date', 'is', null);

      if (currentError) throw currentError;

      // Get previous year sales by category from invoices
      // Use left join to include invoices without items
      const { data: previousYearData, error: previousError } = await supabase
        .from('invoices')
        .select(`
          amount,
          discount_percentage,
          title,
          invoice_items(
            quantity,
            unit_price,
            products(
              product_categories(name)
            )
          )
        `)
        .gte('issue_date', startOfPreviousYear)
        .lte('issue_date', endOfPreviousPeriod)
        .not('issue_date', 'is', null);

      if (previousError) throw previousError;

      // Calculate legacy amounts from invoices without invoice_items
      // We'll do this by finding the difference between total sales and categorized sales
      
      // No need for complex RPC calls, let's use the expected total approach

      // Initialize all predefined categories plus any additional ones found in data
      const categorySales: Record<string, CategorySales> = {};
      
      // First initialize all predefined categories
      PRODUCT_CATEGORIES.forEach(category => {
        categorySales[category] = {
          category,
          currentYearSales: 0,
          previousYearSales: 0,
          growth: 0,
          growthPercentage: 0
        };
      });

      // Add "Uncategorized" for items without category
      categorySales['Uncategorized'] = {
        category: 'Uncategorized',
        currentYearSales: 0,
        previousYearSales: 0,
        growth: 0,
        growthPercentage: 0
      };

      // Collect all unique category names from the data and add any missing ones
      const allCategories = new Set<string>();
      
      // Add categories from current year
      currentYearData?.forEach(invoice => {
        invoice.invoice_items?.forEach(item => {
          if (item.products?.product_categories?.name) {
            const categoryName = item.products.product_categories.name;
            allCategories.add(categoryName);
            if (!categorySales[categoryName]) {
              categorySales[categoryName] = {
                category: categoryName,
                currentYearSales: 0,
                previousYearSales: 0,
                growth: 0,
                growthPercentage: 0
              };
            }
          }
        });
      });
      
      // Add categories from previous year
      previousYearData?.forEach(invoice => {
        invoice.invoice_items?.forEach(item => {
          if (item.products?.product_categories?.name) {
            const categoryName = item.products.product_categories.name;
            allCategories.add(categoryName);
            if (!categorySales[categoryName]) {
              categorySales[categoryName] = {
                category: categoryName,
                currentYearSales: 0,
                previousYearSales: 0,
                growth: 0,
                growthPercentage: 0
              };
            }
          }
        });
      });

      // Helper function to categorize legacy invoices based on title
      const categorizeLegacyInvoice = (title: string | null): string => {
        if (!title) return 'Other Ad-Hoc';
        
        const titleLower = title.toLowerCase();
        
        // Check for Wisdrop
        if (titleLower.includes('wisdrop')) return 'Wisdrop';
        
        // Check for Qualitative keywords
        if (titleLower.includes('digital groups') || 
            titleLower.includes('brand value') || 
            titleLower.includes('qualitative')) return 'Qualitative';
        
        // Check for zevioo
        if (titleLower.includes('zevioo')) return 'zevioo';
        
        // Check for Brand Beat
        if (titleLower.includes('brand beat') || titleLower.includes('brandbeat')) return 'Brand Beat';
        
        // Check for Contracts
        if (titleLower.includes('contract')) return 'Contracts';
        
        // Default to Other Ad-Hoc
        return 'Other Ad-Hoc';
      };

      // Process current year data - distribute invoice amount proportionally across categories
      currentYearData?.forEach(invoice => {
        const invoiceAmount = Number(invoice.amount || 0);
        
        // Check if invoice has items
        if (!invoice.invoice_items || invoice.invoice_items.length === 0) {
          // Legacy invoice without items - categorize based on title
          const category = categorizeLegacyInvoice(invoice.title);
          categorySales[category].currentYearSales += invoiceAmount;
          return;
        }
        
        // Calculate the subtotal from items
        let invoiceSubtotal = 0;
        const itemsByCategory: Record<string, number> = {};
        
        invoice.invoice_items.forEach(item => {
          const categoryName = item.products?.product_categories?.name || 'Uncategorized';
          const itemTotal = Number(item.quantity || 0) * Number(item.unit_price || 0);
          itemsByCategory[categoryName] = (itemsByCategory[categoryName] || 0) + itemTotal;
          invoiceSubtotal += itemTotal;
        });
        
        // Distribute the invoice amount proportionally to each category
        if (invoiceSubtotal > 0) {
          Object.entries(itemsByCategory).forEach(([categoryName, categorySubtotal]) => {
            if (categorySales[categoryName]) {
              const proportion = categorySubtotal / invoiceSubtotal;
              const categoryAmount = invoiceAmount * proportion;
              categorySales[categoryName].currentYearSales += categoryAmount;
            }
          });
        } else {
          // Items exist but subtotal is 0 - add to Uncategorized
          categorySales['Uncategorized'].currentYearSales += invoiceAmount;
        }
      });

      // Process previous year data - distribute invoice amount proportionally across categories
      previousYearData?.forEach(invoice => {
        const invoiceAmount = Number(invoice.amount || 0);
        
        // Check if invoice has items
        if (!invoice.invoice_items || invoice.invoice_items.length === 0) {
          // Legacy invoice without items - categorize based on title
          const category = categorizeLegacyInvoice(invoice.title);
          categorySales[category].previousYearSales += invoiceAmount;
          return;
        }
        
        // Calculate the subtotal from items
        let invoiceSubtotal = 0;
        const itemsByCategory: Record<string, number> = {};
        
        invoice.invoice_items.forEach(item => {
          const categoryName = item.products?.product_categories?.name || 'Uncategorized';
          const itemTotal = Number(item.quantity || 0) * Number(item.unit_price || 0);
          itemsByCategory[categoryName] = (itemsByCategory[categoryName] || 0) + itemTotal;
          invoiceSubtotal += itemTotal;
        });
        
        // Distribute the invoice amount proportionally to each category
        if (invoiceSubtotal > 0) {
          Object.entries(itemsByCategory).forEach(([categoryName, categorySubtotal]) => {
            if (categorySales[categoryName]) {
              const proportion = categorySubtotal / invoiceSubtotal;
              const categoryAmount = invoiceAmount * proportion;
              categorySales[categoryName].previousYearSales += categoryAmount;
            }
          });
        } else {
          // Items exist but subtotal is 0 - add to Uncategorized
          categorySales['Uncategorized'].previousYearSales += invoiceAmount;
        }
      });

      // Calculate growth
      Object.values(categorySales).forEach(category => {
        category.growth = category.currentYearSales - category.previousYearSales;
        category.growthPercentage = category.previousYearSales > 0 
          ? (category.growth / category.previousYearSales) * 100 
          : category.currentYearSales > 0 ? 100 : 0;
      });

      // Create ordered result following the predefined order, showing all categories including those with zero sales
      const orderedResult: CategorySales[] = [];
      
      // First add categories in the predefined order
      PRODUCT_CATEGORIES.forEach(categoryName => {
        if (categorySales[categoryName]) {
          orderedResult.push(categorySales[categoryName]);
        } else {
          // Add category with zero sales if it doesn't exist in data
          orderedResult.push({
            category: categoryName,
            currentYearSales: 0,
            previousYearSales: 0,
            growth: 0,
            growthPercentage: 0
          });
        }
      });
      
      // Then add any additional categories not in the predefined list (e.g., new categories from database)
      Object.values(categorySales).forEach(category => {
        if (!PRODUCT_CATEGORIES.includes(category.category) && category.category !== 'Uncategorized') {
          orderedResult.push(category);
        }
      });
      
      // Finally add Uncategorized if it has any data
      if (categorySales['Uncategorized'] && (categorySales['Uncategorized'].currentYearSales > 0 || categorySales['Uncategorized'].previousYearSales > 0)) {
        orderedResult.push(categorySales['Uncategorized']);
      }
      
      return orderedResult;
    },
    staleTime: 0, // Always fetch fresh data for dashboard
    refetchOnMount: 'always', // Ensure fresh data on component mount
  });

  if (isLoading) return <SalesOverviewSkeleton />;
  
  if (error) {
    console.error('Sales Overview Error:', error);
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sales Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            Failed to load sales overview data
            {error instanceof Error && (
              <div className="text-xs text-red-500 mt-2">{error.message}</div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getTrendIcon = (growthPercentage: number) => {
    if (growthPercentage > 5) return <TrendingUp className="h-3 w-3 text-green-600" />;
    if (growthPercentage < -5) return <TrendingDown className="h-3 w-3 text-red-600" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  const getTrendColor = (growthPercentage: number) => {
    if (growthPercentage > 5) return "text-green-600";
    if (growthPercentage < -5) return "text-red-600";
    return "text-muted-foreground";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sales Overview {selectedYear}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {salesData?.map((category) => (
            <div key={category.category} className="p-4 border rounded-lg bg-card">
              <div className="text-sm font-medium text-muted-foreground mb-1">
                {category.category}
              </div>
              <div className="text-lg font-semibold mb-1">
                {formatCurrency(category.currentYearSales)}
              </div>
              <div className={`flex items-center gap-1 text-xs ${getTrendColor(category.growthPercentage)}`}>
                {getTrendIcon(category.growthPercentage)}
                <span>
                  {Math.abs(category.growthPercentage).toFixed(1)}%
                </span>
                <span className={category.growth >= 0 ? "text-green-600" : "text-red-600"}>
                  ({category.growth >= 0 ? '+' : '-'}{formatCurrency(Math.abs(category.growth))})
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}